import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ActivateUser,
	ActivateUserSuccess,
	DeactivateUser,
	DeactivateUserSuccess,
	DeleteGroup,
	DeleteUser,
	DeleteUserSuccess,
	EEmployeesActions,
	GetAllGroups,
	GetAllGroupsSuccess,
	GetEmployeeSalaryTotalsCalculation,
	GetEmployeeSalaryTotalsCalculationSuccess,
	GetEmployeeSupplementalSalaries,
	GetEmployeeSupplementalSalariesSuccess,
	GetEmployeeSupplementalSalariesWithGroups,
	GetEmployeeSupplementalSalariesWithGroupsSuccess,
	GetGroup,
	GetGroups,
	GetGroupsSuccess,
	GetGroupsSupplementalSalaries,
	GetGroupSuccess,
	GetInformationForCreatingUser,
	GetInformationForCreatingUserSuccess,
	GetSocialSecurityNumber,
	GetSocialSecurityNumberSuccess,
	GetUser,
	GetUsers,
	GetUsersSuccess,
	GetUserSuccess,
	PatchGroup,
	PatchGroupFiles,
	PatchGroupFilesSuccess,
	PatchGroupSuccess,
	PatchProfile,
	PatchProfileSuccess,
	PatchUser,
	PatchUserSuccess,
	PostGroup,
	PostGroupSuccess,
	PostUser,
	PostUserSuccess,
	SendActivationMail,
	SendActivationMailSuccess,
	SetEditingGroup,
	SetEditingUser,
	SetIsSeeingEmployees,
} from '@app/core/store/actions/employees.actions';
import {UserService} from '@app/core/services/api/user.service';
import {User} from '@app/core/models/user.model';
import {GroupService} from '@app/core/services/api/group.service';
import {Group} from '@app/core/models/group.model';
import {Router} from '@angular/router';
import {DepartmentService} from '@app/core/services/api/department.service';
import {Department} from '@app/core/models/department.model';
import {SnackBar} from '@app/core/services/snackbar.service';
import {SupplementalSalary} from '@app/core/models/supplementalSalary.model';
import {PaginationHolder} from '@app/core/models/pagination.model';
import {SupplementalSalaryService} from '@app/core/services/api/supplementalSalary.service';
import {IEmployeeSalaryCalculation} from '@app/core/store/state/employees.state';
import {EQOLogLevel, QOLogService} from '@quickorder/angular-web-lib';
import {TranslateService} from '@ngx-translate/core';

@Injectable()
export class EmployeesEffects {

	@Effect()
	public onGetUsers = this.actions$.pipe(
		ofType<GetUsers>(EEmployeesActions.GetUsers),
		switchMap((action: GetUsers) => from(this.userService.getUsers({
				'filter[departments.id]': action.departmentIdsFormatted,
			}, 200, 1, action.includeDeactivated).pipe(
			switchMap((users: User[]) => [
				new GetUsersSuccess(users),
			]),
			catchError(error => of(new LoadError(error, action))),
			)),
		),
	);

	@Effect()
	public onGetUser = this.actions$.pipe(
		ofType<GetUser>(EEmployeesActions.GetUser),
		switchMap((action: GetUser) => from(this.userService.getUser(action.userId).pipe(
			switchMap((user: User) => [
				new GetUserSuccess(user),
				new SetEditingUser(user),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onPostUser = this.actions$.pipe(
		ofType<PostUser>(EEmployeesActions.PostUser),
		switchMap((action: PostUser) => from(this.userService.postUser(action.payload).pipe(
			switchMap((user: any) => {
				this.snackBar.newSuccess('EMPLOYEE.CREATED');
				this.router.navigate(['/home/employees/employee/' + user.id]);

				return [
					new SetIsSeeingEmployees(true),
					new SetEditingUser(undefined),
					new PostUserSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onPatchUser = this.actions$.pipe(
		ofType<PatchUser>(EEmployeesActions.PatchUser),
		switchMap((action: PatchUser) => from(this.userService.patchUser(action.userId, action.payload).pipe(
			switchMap((user) => {

				if (action.emailChanged) {
					this.logService.logContent({
						level: EQOLogLevel.INFO,
						title:  this.translate.instant('SNACKBAR.DONE'),
						description: this.translate.instant('EMAIL_VERIFICATION.EMAIL_VERIFICATION_REQUIRED'),
						forceDuration: 999999,
						actions: [],
					});
				} else {
					this.snackBar.newSuccess('EMPLOYEE.UPDATED');
				}

				this.router.navigate(['/home/employees/']);

				return [
					new SetIsSeeingEmployees(true),
					new SetEditingUser(undefined),
					new PatchUserSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onPatchProfile = this.actions$.pipe(
		ofType<PatchProfile>(EEmployeesActions.PatchProfile),
		switchMap((action: PatchProfile) => from(this.userService.patchProfile(action.payload).pipe(
			switchMap((res) => {

				this.router.navigate(['/home/employees/']);

				return [
					new SetIsSeeingEmployees(true),
					new SetEditingUser(undefined),
					new PatchProfileSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onDeleteUser = this.actions$.pipe(
		ofType<DeleteUser>(EEmployeesActions.DeleteUser),
		switchMap((action: DeleteUser) => from(this.userService.deleteUser(action.userId).pipe(
			switchMap(() => {
				this.snackBar.newSuccess('EMPLOYEE.DELETED');

				return [
					new DeleteUserSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onDeactivateUser = this.actions$.pipe(
		ofType<DeactivateUser>(EEmployeesActions.DeactivateUser),
		switchMap((action: DeactivateUser) => from(this.userService.patchUser(action.userId, {deactivated: true}).pipe(
			switchMap(() => {
				this.snackBar.newSuccess('EMPLOYEE.USER_DEACTIVATED');

				return [
					new DeactivateUserSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onActivateUser = this.actions$.pipe(
		ofType<ActivateUser>(EEmployeesActions.ActivateUser),
		switchMap((action: ActivateUser) => from(this.userService.patchUser(action.userId, {deactivated: false}).pipe(
			switchMap(() => {
				this.snackBar.newSuccess('EMPLOYEE.USER_ACTIVATED');

				return [
					new ActivateUserSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onSendActivationMail = this.actions$.pipe(
		ofType<SendActivationMail>(EEmployeesActions.SendActivationMail),
		switchMap((action: SendActivationMail) => from(this.userService.sendActivationMail(action.user).pipe(
			switchMap(() => {
				this.snackBar.newSuccess('contact_emailSend');

				return [
					new SendActivationMailSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetGroups = this.actions$.pipe(
		ofType<GetGroups>(EEmployeesActions.GetGroups),
		switchMap((action: GetGroups) => from(this.groupService.getGroups({
			'filter[department.id]': action.departmentIdsFormatted,
			include: 'users_count, department, users',
		}, 200, 1).pipe(
			switchMap((groups: Group[]) => [
				new GetGroupsSuccess(groups),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetAllGroups$ = this.actions$.pipe(
		ofType<GetAllGroups>(EEmployeesActions.GetAllGroups),
		switchMap((action: GetAllGroups) => from(this.groupService.getGroups({
			include: 'users_count, department, users',
		}, 200, 1).pipe(
			switchMap((groups: Group[]) => [
				new GetAllGroupsSuccess(groups),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetGroup = this.actions$.pipe(
		ofType<GetGroup>(EEmployeesActions.GetGroup),
		switchMap((action: GetGroup) => from(this.groupService.getGroup(action.groupId, action.filters).pipe(
			switchMap((group: Group) => [
				new GetGroupSuccess(group),
				new SetEditingGroup(group),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onPostGroup = this.actions$.pipe(
		ofType<PostGroup>(EEmployeesActions.PostGroup),
		switchMap((action: PostGroup) => from(this.groupService.postGroup(action.payload).pipe(
			switchMap((res) => {
				this.snackBar.newSuccess('GROUP.CREATED');
				this.router.navigate(['/home/employees']);

				return [
					new SetIsSeeingEmployees(false),
					new SetEditingGroup(null),
					new PostGroupSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onPatchGroup = this.actions$.pipe(
		ofType<PatchGroup>(EEmployeesActions.PatchGroup),
		switchMap((action: PatchGroup) => from(this.groupService.patchGroup(action.groupId, action.payload).pipe(
			switchMap((res) => {
				this.snackBar.newSuccess('GROUP.UPDATED');
				this.router.navigate(['/home/employees/']);

				return [
					new SetIsSeeingEmployees(false),
					new SetEditingGroup(null),
					new PatchGroupSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onPatchGroupFiles = this.actions$.pipe(
		ofType<PatchGroupFiles>(EEmployeesActions.PatchGroupFiles),
		switchMap((action: PatchGroupFiles) => from(this.groupService.patchGroup(action.groupId, action.payload).pipe(
			switchMap((res) => {
				this.snackBar.newSuccess('GROUP.UPDATED');
				return [
					new SetIsSeeingEmployees(false),
					new PatchGroupFilesSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onDeleteGroup = this.actions$.pipe(
		ofType<DeleteGroup>(EEmployeesActions.DeleteGroup),
		switchMap((action: DeleteGroup) => from(this.groupService.deleteGroup(action.groupId).pipe(
			switchMap((group) => {
				this.snackBar.newSuccess('GROUP.DELETED');
				return [
					new GetGroups([]),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetInformationForCreatingUser = this.actions$.pipe(
		ofType<GetInformationForCreatingUser>(EEmployeesActions.GetInformationForCreatingUser),
		switchMap((action: GetInformationForCreatingUser) => from(this.departmentService.getInformationForCreatingUser().pipe(
			switchMap((departments: Department[]) => [
				new GetInformationForCreatingUserSuccess(departments),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetSocialSecurityNumber = this.actions$.pipe(
		ofType<GetSocialSecurityNumber>(EEmployeesActions.GetSocialSecurityNumber),
		switchMap((action: GetSocialSecurityNumber) => from(this.userService.getSocialSecurityNumber(action.userId).pipe(
			switchMap((res) => [
				new GetSocialSecurityNumberSuccess(res.social_security_number),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetEmployeeSupplementalSalaries$ = this.actions$.pipe(
		ofType<GetEmployeeSupplementalSalaries>(EEmployeesActions.GetEmployeeSupplementalSalaries),
		switchMap((action: GetEmployeeSupplementalSalaries) => from(this.supplementalSalaryService.getSupplementalSalaries(action.page, action.page_size, action.filters).pipe(
			switchMap((res: PaginationHolder<SupplementalSalary>) => [
				new GetEmployeeSupplementalSalariesSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetGroupSupplementalSalaries$ = this.actions$.pipe(
		ofType<GetGroupsSupplementalSalaries>(EEmployeesActions.GetGroupsSupplementalSalaries),
		switchMap((action: GetGroupsSupplementalSalaries) => from(this.supplementalSalaryService.getSupplementalSalaries(action.page, action.page_size, action.filters).pipe(
			switchMap((res: PaginationHolder<SupplementalSalary>) => [
				new GetEmployeeSupplementalSalariesSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetEmployeeSupplementalSalariesWithGroups$ = this.actions$.pipe(
		ofType<GetEmployeeSupplementalSalariesWithGroups>(EEmployeesActions.GetEmployeeSupplementalSalariesWithGroups),
		switchMap((action: GetEmployeeSupplementalSalariesWithGroups) => from(this.supplementalSalaryService.getSupplementalSalaries(action.page, action.page_size, action.filters).pipe(
			switchMap((res: PaginationHolder<SupplementalSalary>) => [
				new GetEmployeeSupplementalSalariesWithGroupsSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	@Effect()
	public onGetEmployeeSalaryTotalsCalculation$ = this.actions$.pipe(
		ofType<GetEmployeeSalaryTotalsCalculation>(EEmployeesActions.GetEmployeeSalaryTotalsCalculation),
		switchMap((action: GetEmployeeSalaryTotalsCalculation) => from(this.userService.getEmployeeSalaryTotalsCalculation(action.userId, action.year, action.month, action.includeShifts).pipe(
			switchMap((salaryTotals: IEmployeeSalaryCalculation) =>
				[
					new GetEmployeeSalaryTotalsCalculationSuccess(action.month, salaryTotals),
				]),
			catchError(error => of(new LoadError(error, action))),
		))),
	);

	constructor(
		private actions$: Actions,
		private userService: UserService,
		private groupService: GroupService,
		private departmentService: DepartmentService,
		private supplementalSalaryService: SupplementalSalaryService,
		private router: Router,
		private snackBar: SnackBar,
		private logService: QOLogService,
		private translate: TranslateService,
	) {
	}
}
